import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

const BASE = '/api/portal';

export interface PortalUser {
  id: string;
  email: string;
  clientName: string;
  contactPerson: string;
}

export interface PortalProject {
  id: string;
  name: string;
  description: string;
  status: string;
  priority: string;
  startDate: string;
  endDate: string;
  milestones: { id: string; title: string; dueDate: string; done: boolean }[];
}

export interface PortalInvoice {
  id: string;
  title: string;
  status: string;
  amount: number;
  referenceNumber: string;
  validUntil: string | null;
  issuedAt: string;
}

export interface PortalTicket {
  id: string;
  ticketNumber: string;
  title: string;
  description: string;
  priority: string;
  status: string;
  contractTier: string;
  firstResponseDueAt: string;
  resolutionDueAt: string;
  resolvedAt: string | null;
  resolutionSummary: string;
  createdAt: string;
  sla: { state: string; percent: number; threshold: number; remainingMinutes: number | null };
}

export interface PortalOverview {
  client: PortalUser;
  projects: PortalProject[];
  invoices: PortalInvoice[];
  tickets: PortalTicket[];
}

export interface PortalAccount {
  id: string;
  email: string;
  clientName: string;
  contactPerson: string;
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export const portalKeys = {
  root: ['portal'] as const,
  me: ['portal', 'me'] as const,
  overview: ['portal', 'overview'] as const,
  adminUsers: ['portal', 'admin', 'users'] as const,
};

export function usePortalMe() {
  return useQuery({
    queryKey: portalKeys.me,
    queryFn: async () => (await api.get<PortalUser>(`${BASE}/auth/me`)).data,
    retry: false,
  });
}

export function usePortalOverview(enabled: boolean) {
  return useQuery({
    queryKey: portalKeys.overview,
    queryFn: async () => (await api.get<PortalOverview>(`${BASE}/overview`)).data,
    enabled,
  });
}

export function usePortalMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: portalKeys.root });

  const login = useMutation({
    mutationFn: async (payload: { email: string; password: string }) =>
      (await api.post<PortalUser>(`${BASE}/auth/login`, payload)).data,
    onSuccess: refresh,
  });

  const logout = useMutation({
    mutationFn: async () => (await api.post(`${BASE}/auth/logout`)).data,
    onSuccess: refresh,
  });

  const raiseTicket = useMutation({
    mutationFn: async (payload: { title: string; description: string }) =>
      (await api.post<PortalTicket>(`${BASE}/tickets`, payload)).data,
    onSuccess: refresh,
  });

  return { login, logout, raiseTicket };
}

/* ── Internal admin (staff session) ──────────────────────────────────────── */

export function usePortalAccounts() {
  return useQuery({
    queryKey: portalKeys.adminUsers,
    queryFn: async () => (await api.get<PortalAccount[]>(`${BASE}/admin/users`)).data,
  });
}

export function usePortalAccountMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: portalKeys.adminUsers });

  const createAccount = useMutation({
    mutationFn: async (payload: { email: string; password: string; clientName: string; contactPerson?: string }) =>
      (await api.post(`${BASE}/admin/users`, payload)).data,
    onSuccess: refresh,
  });

  const updateAccount = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; active?: boolean; password?: string; clientName?: string }) =>
      (await api.patch(`${BASE}/admin/users/${id}`, payload)).data,
    onSuccess: refresh,
  });

  return { createAccount, updateAccount };
}
